import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

/**
 * useSidebarOrder — persists sidebar item ordering per user.
 * 
 * @param {string} pageKey - unique key per page: 'searches', 'pipelines', 'firms', 'tags'
 * @returns {[string[]|null, (newOrder: string[]) => void, boolean]}
 *   [savedOrder, setSavedOrder, loading]
 * 
 * savedOrder is null until loaded (meaning "use default order").
 * setSavedOrder updates local state immediately and debounce-saves to backend.
 * 
 * Stored as preferences.sidebar_searches, preferences.sidebar_pipelines, etc.
 */
export default function useSidebarOrder(pageKey) {
  const prefKey = `sidebar_${pageKey}`;
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);

  // Load on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) { setLoading(false); return; }

        const res = await fetch(`${API}/api/users/preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const prefs = await res.json();
          if (!cancelled && prefs[prefKey]) {
            setOrder(prefs[prefKey]);
          }
        }
      } catch (err) {
        console.error('Error loading sidebar order:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [prefKey]);

  // Save with debounce
  const saveToBackend = useCallback((newOrder) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        await fetch(`${API}/api/users/preferences`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            key: prefKey,
            value: newOrder,
          }),
        });
      } catch (err) {
        console.error('Error saving sidebar order:', err);
      }
    }, 500);
  }, [prefKey]);

  // Update local + save
  const updateOrder = useCallback((newOrder) => {
    setOrder(newOrder);
    saveToBackend(newOrder);
  }, [saveToBackend]);

  // Cleanup timer
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  return [order, updateOrder, loading];
}
